import { keepPreviousData } from "@tanstack/react-query";
import concat from "lodash/concat";
import { useApiFilters, useSyncFiltersV2Meta } from "PFApp/components/filters_v2";
import { useGrowlStoreActions } from "PFApp/stores/growl_store";
import { hasDefaultValue, isValueEmpty } from "PFCore/helpers/filters";
import { getMatchDisplayValue } from "PFCore/helpers/match_value";
import { useResponseErrors } from "PFCore/helpers/use_response_errors";
import { useMatchesInfinite } from "PFCore/hooks/queries/matches/use_matches";
import { KnownFiltersV2 } from "PFCore/types/filters_v2";
import { MatchedProfile, Meta, OrderMeta, OrderOption, QueryParams } from "PFTypes";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

const PER_PAGE = 10;

const formatScore = (score: number | null | undefined) => getMatchDisplayValue(score, { digits: 0 });

export type MatchesTableEntry = {
  profile: MatchedProfile["profile"];
  matchScore: string | undefined;
  availabilityScore: string | undefined;
};

export type MatchesResponse = {
  matchesMeta: Meta;
  matchesData: MatchesTableEntry[];
  isFetching: boolean;
  isFetchingNextPage: boolean;
  isError: boolean;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  lastRefreshAt: string | null;
};

type MatchesOrder = {
  orderMeta: OrderMeta | undefined;
  handleChangeOrder: (value: OrderOption) => void;
};

type UseResourceMatches = {
  excludedProfileIds: Set<number>;
  activityId: number;
};

type UseResourceMatchesReturn = {
  matchesResponse: MatchesResponse;
  matchesQueryParams: QueryParams;
  matchesOrder: MatchesOrder;
};

export const useResourceMatches = ({
  excludedProfileIds,
  activityId
}: UseResourceMatches): UseResourceMatchesReturn => {
  const { t } = useTranslation();
  const { showGrowl } = useGrowlStoreActions();
  const { getFirstError } = useResponseErrors();
  const [matchesQueryParamsWithoutFilters, setMatchesQueryParamsWithoutFilters] = useState<
    Omit<QueryParams, "filters">
  >({
    perPage: PER_PAGE
  });
  const { apiFilters } = useApiFilters({ sendDefaultsAsUndefined: false, sendEmptyAsUndefined: false });

  const matchesQueryParams = useMemo(
    () => ({
      ...matchesQueryParamsWithoutFilters,
      filters: apiFilters
    }),
    [matchesQueryParamsWithoutFilters, apiFilters]
  );

  const updateMatchesParams = (params: QueryParams) =>
    setMatchesQueryParamsWithoutFilters((currentParams) => ({
      ...currentParams,
      ...params
    }));

  const {
    data: matchesPaginatedData,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isError,
    error
  } = useMatchesInfinite(activityId, matchesQueryParams, {
    placeholderData: keepPreviousData,
    retry: false
  });

  const mergedPages = concat(...(matchesPaginatedData?.pages.map(({ entries }) => entries) || []));

  const meta = useMemo(
    () =>
      matchesPaginatedData && matchesPaginatedData.pages.length > 0
        ? matchesPaginatedData?.pages[0].meta
        : ({ total: 0 } as Meta),
    [matchesPaginatedData]
  );

  const syncFiltersV2 = useSyncFiltersV2Meta();
  useEffect(() => {
    const availabilityFilter = meta?.filtersV2?.mainFilters?.[KnownFiltersV2.Availability];
    if (availabilityFilter && hasDefaultValue(availabilityFilter) && isValueEmpty(availabilityFilter.value)) {
      // eslint-disable-next-line react-hooks/immutability
      availabilityFilter.value = availabilityFilter.defaultValue!;
    }
    syncFiltersV2(meta);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta]);

  useEffect(() => {
    if (error) {
      const errorMessage = getFirstError(error);
      showGrowl({
        message: errorMessage || t("somethingWentWrong"),
        kind: "error"
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const lastRefreshAt =
    meta && meta.timestamps && "matched_at" in meta.timestamps ? meta.timestamps.matched_at : null;

  const matchesData = mergedPages
    .filter(({ profile }) => !excludedProfileIds.has(profile.id))
    .map(({ profile, scores }) => ({
      profile,
      matchScore: formatScore(scores.normalizedScore),
      availabilityScore: formatScore(scores.availabilityScore)
    }));

  const orderMeta = meta?.order;

  const handleChangeOrder = (value: OrderOption) => updateMatchesParams({ order: value });

  const matchesResponse = {
    matchesMeta: meta,
    matchesData,
    fetchNextPage,
    hasNextPage: !!hasNextPage,
    isFetching,
    isFetchingNextPage,
    isError,
    lastRefreshAt
  };

  const matchesOrder = {
    orderMeta,
    handleChangeOrder
  };

  return {
    matchesResponse,
    matchesQueryParams,
    matchesOrder
  };
};
